/**
 * Faz V: Web SPA'nın "kendisini" backend'e bir controller device olarak
 * kaydeder. Signaling handshake'e register + offer gönderebilmek için zorunlu
 * — signaling server Device.OwnerUserId ile JWT'deki sub alanını karşılaştırır.
 *
 * Akış:
 *   1. IndexedDB'de "photon.web-controller.key" varsa yükle, yoksa
 *      @noble/curves Ed25519 ile üret + kaydet (32 byte secret).
 *   2. localStorage "photon.web-controller.deviceId" varsa o ID ile devam et.
 *   3. Yoksa POST /devices ile register + deviceId'yi sakla.
 *
 * NOT: Private key tarayıcıda signing yapmak için saklanır. Şu an signaling
 * register device_jwt=empty'yi kabul ediyor (auth JWT ile user sahipliği
 * doğrulanıyor) — ileride device-signed nonce challenge eklenirse kullanılır.
 */
import { ed25519 } from '@noble/curves/ed25519';
import { appState } from '../state';
const LS_DEVICE_ID = 'photon.web-controller.deviceId';
const LS_HUMAN_ID = 'photon.web-controller.humanId';
const IDB_DB = 'photon-remote';
const IDB_STORE = 'web-controller';
const IDB_KEY = 'ed25519-secret'; // legacy noble path (raw 32B)
const IDB_KEY_CK = 'ed25519-cryptokey'; // B4 #19 modern path (CryptoKey)
const IDB_KEY_PUB = 'ed25519-public'; // public key cache (CryptoKey path)
/** Base64-url decode için yardımcı. */
function b64(bytes) {
    let s = '';
    for (const b of bytes)
        s += String.fromCharCode(b);
    return btoa(s);
}
async function idbOpen() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(IDB_DB, 1);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(IDB_STORE)) {
                db.createObjectStore(IDB_STORE);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('idb open fail'));
    });
}
async function idbGet(key) {
    const db = await idbOpen();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readonly');
        const store = tx.objectStore(IDB_STORE);
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => reject(req.error);
    });
}
async function idbPut(key, value) {
    const db = await idbOpen();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        const store = tx.objectStore(IDB_STORE);
        const req = store.put(value, key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}
/**
 * B4 #19: Browser Ed25519 non-extractable destekliyor mu? Chrome 127+ stable,
 * Safari 17+ partial. Test: generateKey çağrısı try-catch — destek yoksa
 * NotSupportedError fırlatır, noble fallback path'e düşeriz.
 */
async function trySubtleEd25519GenerateKey() {
    try {
        const kp = await crypto.subtle.generateKey({ name: 'Ed25519' }, false, // extractable=false → key material JS'e çıkmaz
        ['sign', 'verify']);
        // Public key'i raw export edebilelim (extractable=false private + extractable
        // public default subtle davranışı). Sanity check:
        await crypto.subtle.exportKey('raw', kp.publicKey);
        return kp;
    }
    catch {
        return null;
    }
}
/**
 * Ed25519 key çiftini yükler veya üretir. Key değişmez: bir kez üretildikten
 * sonra aynı tarayıcı profili aynı key'i kullanır.
 *
 * B4 #19: Modern path → subtle.CryptoKey (non-extractable, IndexedDB'de
 * structured-clone ile sarılı saklanır, raw bytes JS'e çıkmaz). XSS olsa bile
 * attacker key material'ı dump edemez, sadece sign() çağrılarını proxy'leyebilir.
 *
 * Eski browser fallback → @noble/curves raw secret IndexedDB'de (mevcut davranış).
 */
async function loadOrGenerateKey() {
    // 1) Daha önce hardened path ile saklanmış bir CryptoKey var mı?
    const ckExisting = await idbGet(IDB_KEY_CK);
    const pubExisting = await idbGet(IDB_KEY_PUB);
    if (ckExisting && pubExisting && pubExisting.length === 32) {
        return {
            hardened: true,
            cryptoPrivate: ckExisting,
            publicKey: pubExisting,
        };
    }
    // 2) Legacy noble secret var mı (eski tarayıcıdan kalma)? — Geçişi bozmamak
    // için aynen kullan; non-extractable'a migrate etme (key change = device
    // re-register gerek, kullanıcı oturumu kopar).
    const legacyExisting = await idbGet(IDB_KEY);
    if (legacyExisting && legacyExisting.length === 32) {
        const publicKey = ed25519.getPublicKey(legacyExisting);
        return { hardened: false, secret: legacyExisting, publicKey };
    }
    // 3) Yeni key — önce subtle non-extractable dene, başarısızsa noble.
    const kp = await trySubtleEd25519GenerateKey();
    if (kp) {
        const pubRaw = new Uint8Array(await crypto.subtle.exportKey('raw', kp.publicKey));
        await idbPut(IDB_KEY_CK, kp.privateKey);
        await idbPut(IDB_KEY_PUB, pubRaw);
        return { hardened: true, cryptoPrivate: kp.privateKey, publicKey: pubRaw };
    }
    const secret = ed25519.utils.randomPrivateKey();
    await idbPut(IDB_KEY, secret);
    const publicKey = ed25519.getPublicKey(secret);
    return { hardened: false, secret, publicKey };
}
/** signMessage helper — modern path subtle.sign, legacy noble.sign. */
function makeSigner(k) {
    if (k.hardened && k.cryptoPrivate) {
        const ck = k.cryptoPrivate;
        return async (msg) => {
            // BufferSource type ArrayBuffer'a göre kısıtlı; Uint8Array'in buffer'ı
            // ArrayBufferLike olabilir (SharedArrayBuffer sayılır). Açıkça yeni
            // bir ArrayBuffer'a kopyala — copyBytesFrom yerine slice yeterli.
            const buf = msg.slice().buffer;
            return new Uint8Array(await crypto.subtle.sign('Ed25519', ck, buf));
        };
    }
    if (k.secret) {
        const s = k.secret;
        return async (msg) => ed25519.sign(msg, s);
    }
    throw new Error('no signing key available');
}
/**
 * Web SPA controller device'ı hazırlar. Backend'e register olmuşsa mevcut
 * deviceId'yi kullanır; aksi halde yeni kayıt açar. Aynı user aynı browser
 * bir kez register olur.
 */
export async function ensureWebControllerDevice() {
    const k = await loadOrGenerateKey();
    const signMessage = makeSigner(k);
    const cachedDeviceId = localStorage.getItem(LS_DEVICE_ID);
    const cachedHumanId = localStorage.getItem(LS_HUMAN_ID);
    if (cachedDeviceId && cachedHumanId) {
        // v0.2.44: Eski deviceId backend'de hâlâ var mı / sahibi ben miyim
        // probe et. Önceden body olmadan localStorage'dan dönüyorduk; eski
        // device DB'den silinmişse (admin temizlik / orphan cleanup vb.)
        // signaling "Device ownership reddedildi" diyor → register fail →
        // session açılmıyor → siyah ekran. Probe başarısız → yeni device kayıt.
        const ok = await probeDeviceValid(cachedDeviceId);
        if (ok) {
            return {
                deviceId: cachedDeviceId,
                humanId: cachedHumanId,
                secretKey: k.secret ?? null,
                publicKey: k.publicKey,
                hardened: k.hardened,
                signMessage,
            };
        }
        // Stale localStorage entry — temizle, alttaki kayıt akışına düş.
        localStorage.removeItem(LS_DEVICE_ID);
        localStorage.removeItem(LS_HUMAN_ID);
        console.warn('[ensureWebControllerDevice] cached deviceId stale (DB temiz/orphan), yeni kayıt açılıyor');
    }
    // Backend /devices register — Ed25519 pubkey 32 byte base64.
    const pubB64 = b64(k.publicKey);
    const os = detectOs();
    const displayName = `PhotonRemote Web (${os})`;
    const r = await fetch(`${appState.backendUrl}/devices`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${appState.accessToken}`,
        },
        body: JSON.stringify({
            displayName,
            os,
            devicePublicKeyBase64: pubB64,
        }),
    });
    if (!r.ok) {
        throw new Error(`web controller device register fail: ${r.status} ${await r.text()}`);
    }
    const data = await r.json();
    localStorage.setItem(LS_DEVICE_ID, data.deviceId);
    localStorage.setItem(LS_HUMAN_ID, data.humanId);
    return {
        deviceId: data.deviceId,
        humanId: data.humanId,
        secretKey: k.secret ?? null,
        publicKey: k.publicKey,
        hardened: k.hardened,
        signMessage,
    };
}
/**
 * v0.2.44: Backend'e cachedDeviceId ile bir GET atıp owner'ı biz miyiz
 * doğrula. /devices/{id}/monitors authenticated + ownership check'li bir
 * endpoint; 404 → yok ya da bana ait değil, 200 → benim. 401 (token expired)
 * durumunda valid sayalım — token sorunu zaten ayrı flow ile çözülüyor.
 */
async function probeDeviceValid(deviceId) {
    try {
        const r = await fetch(`${appState.backendUrl}/devices/${deviceId}/monitors`, {
            headers: {
                'Authorization': `Bearer ${appState.accessToken}`,
            },
        });
        if (r.status === 200)
            return true;
        if (r.status === 401 || r.status === 403)
            return true; // token issue, device not the issue
        return false; // 404 / 5xx → invalid
    }
    catch {
        // Network error — fail open (varsa kullan, sonra connect zaten patlar)
        return true;
    }
}
function detectOs() {
    const ua = navigator.userAgent;
    if (/Windows/i.test(ua))
        return 'Web (Windows)';
    if (/Mac/i.test(ua))
        return 'Web (macOS)';
    if (/Linux/i.test(ua))
        return 'Web (Linux)';
    if (/Android/i.test(ua))
        return 'Web (Android)';
    if (/iPhone|iPad/i.test(ua))
        return 'Web (iOS)';
    return 'Web';
}
